import { useState } from "react";
import { useNavigate, useLocation, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Eye, EyeOff, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import logo from "@/assets/freshezy-logo.webp";

export default function Login() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);

  if (user) return <Navigate to="/" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const res = await login(email.trim(), password);
    setBusy(false);
    if (res.ok) {
      toast.success("Welcome back");
      const dest = location.state?.from?.pathname || "/";
      navigate(dest, { replace: true });
    } else {
      toast.error(res.message);
    }
  };

  return (
    <div className="flex min-h-screen">
      {/* Brand panel */}
      <div className="relative hidden w-1/2 flex-col justify-between overflow-hidden bg-[#1E1B18] p-12 lg:flex">
        <div
          className="absolute inset-0 opacity-25"
          style={{
            backgroundImage:
              "url('https://images.unsplash.com/photo-1732565729552-994c6af761e3?crop=entropy&cs=srgb&fm=jpg&q=80')",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#1E1B18] via-[#1E1B18]/70 to-transparent" />
        <div className="relative flex items-center gap-3">
          <div className="flex items-center justify-center rounded-xl bg-white p-2 shadow-lg">
            <img src={logo} alt="Freshezy Foodtech Pvt Ltd" className="h-11 w-11 object-contain" />
          </div>
          <div>
            <div className="font-display text-lg font-bold text-white">Freshezy Foodtech Pvt Ltd</div>
            <div className="text-xs text-amber-500/90">Bakery ERP · Web</div>
          </div>
        </div>
        <div className="relative">
          <h1 className="font-display text-4xl font-bold leading-tight text-white">
            Crafting Quality.<br />Delivering Happiness.
          </h1>
          <p className="mt-4 max-w-md text-sm text-stone-300">
            The web workspace for your Bakery ERP — production, inventory, purchasing, sales,
            costing and compliance, all ledger-backed in one place.
          </p>
          <div className="mt-6 inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1.5 text-xs text-amber-200">
            <ShieldCheck className="h-4 w-4" /> Secure · Role-based access
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="flex w-full items-center justify-center bg-[#FAF8F5] p-6 lg:w-1/2">
        <div className="w-full max-w-md">
          <div className="mb-6 flex items-center gap-3">
            <div className="flex items-center justify-center rounded-xl border border-stone-200 bg-white p-1.5 shadow-sm">
              <img src={logo} alt="Freshezy Foodtech Pvt Ltd" className="h-12 w-12 object-contain" />
            </div>
            <span className="font-display text-lg font-bold text-stone-900">Freshezy Foodtech Pvt Ltd</span>
          </div>
          <h2 className="font-display text-2xl font-bold text-stone-900">Sign in</h2>
          <p className="mt-1 text-sm text-muted-foreground">Access your ERP workspace.</p>

          <form onSubmit={onSubmit} className="mt-8 space-y-5">
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                data-testid="login-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                className="h-11"
                autoComplete="username"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  data-testid="login-password"
                  type={showPw ? "text" : "password"}
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="h-11 pr-10"
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  data-testid="toggle-password"
                  onClick={() => setShowPw((s) => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600"
                >
                  {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <Button
              type="submit"
              data-testid="login-submit"
              disabled={busy}
              className="h-11 w-full bg-amber-700 text-sm font-semibold hover:bg-amber-800"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign in"}
            </Button>
          </form>
          <p className="mt-6 text-center text-xs text-muted-foreground">
            Ledger-backed Bakery ERP — secure, role-based access.
          </p>
        </div>
      </div>
    </div>
  );
}
